import type { UiLocale } from '../../conversation/coachCatalog.js';
import type {
  CanDoCapstoneStatus,
  CanDoLessonStatus,
  CanDoNextReason,
  CanDoUnitSummary,
  LocalizedText,
} from '../../../lib/canDoApi.js';

/**
 * Every string of the can-do path's UI (plan 46, P5): the path screen, the
 * unit panel, the grammar library, the capstone briefing, the debrief section,
 * the home card and the radar link. Spanish first (speak's UI is es-first,
 * rioplatense voseo); the English pair is kept in step for when the UI has a
 * locale. The learner copy that comes from the curriculum (can-dos, titles,
 * blurbs) arrives as `{ es, en }` and goes through `loc`.
 */
export interface CanDoCopy {
  eyebrow: string;
  title: string;
  back: string;
  close: string;
  retry: string;
  /** Neutral (the Program's own wording): it shows before the flag is known. */
  loadingProgram: string;
  pathError: string;
  /** The level header reads "{N} {tail}": "2 de 10 logrados". */
  levelAchievedTail: (total: number) => string;
  skillsUsed: (used: number, total: number, level: string) => string;
  levelTabs: string;
  unitKicker: (order: number) => string;
  unitMeta: (order: number, level: string) => string;
  /** The first-steps track (plan 48, phase 4): its tab, its header line, a unit's meta. */
  firstStepsTab: string;
  firstStepsIntro: string;
  trackUnitMeta: (order: number) => string;
  unitStatus: Record<CanDoUnitSummary['status'], string>;
  assumedChip: string;
  assumedNote: string;
  nextChip: string;
  nextEyebrow: string;
  nextReason: Record<CanDoNextReason, string>;
  stepKind: { lesson: string; capstone: string };
  start: string;
  seeMission: string;
  starting: string;
  startError: string;
  levelComplete: (level: string) => string;
  noUnits: (level: string) => string;
  libraryButton: string;
  libraryButtonSub: string;
  lessonsProgress: (done: number, total: number) => string;
  capstoneShort: Record<CanDoCapstoneStatus, string>;
  // Unit panel
  why: string;
  loadingUnit: string;
  unitError: string;
  lessonsEyebrow: string;
  lessonsDone: string;
  focus: (label: string) => string;
  lessonStatus: Record<CanDoLessonStatus, string>;
  objectivesLine: (status: CanDoLessonStatus, met: number, total: number) => string;
  alreadyUsed: string;
  read: string;
  readingLabel: (title: string) => string;
  reviewAlso: string;
  practise: string;
  review: string;
  capstoneEyebrow: string;
  missionCount: (primary: number, bonus: number) => string;
  capstoneState: (c: {
    status: CanDoCapstoneStatus;
    ready: boolean;
    attempts: number;
    best: number;
    total: number;
  }) => string;
  grammarEyebrow: string;
  libraryLink: string;
  words: (used: number, total: number) => string;
  wordsHint: string;
  // Library
  libraryTitle: string;
  libraryIntro: string;
  yourLevel: string;
  readings: (n: number) => string;
  practisedIn: string;
  notPractised: string;
  libraryEmpty: string;
  loadingLibrary: string;
  libraryError: string;
  // Reader
  readingsLabel: string;
  loadingReading: string;
  readingError: string;
  // Capstone briefing
  briefingLabel: string;
  briefingBack: string;
  briefingUnit: string;
  watchedTitle: string;
  watchedNote: string;
  sceneStartError: string;
  // Debrief
  debriefEyebrow: string;
  debriefStep: { lesson: (title: string) => string; capstone: (title: string) => string };
  unitCompletedTitle: string;
  unitCompletedSub: (canDo: string) => string;
  capstonePassedNow: string;
  capstonePending: string;
  continueWith: (title: string) => string;
  /** Sub-line of a radar step that names its skill: the lesson it happens in. */
  radarStepIn: (lessonTitle: string) => string;
  // Home
  homeBadge: string;
  homeTitle: string;
  homeAll: string;
  homeOpen: string;
  homeLevelComplete: (level: string) => string;
  homeLevelCompleteMeta: string;
  homeNoUnitsTitle: string;
  homeNoUnitsMeta: (level: string) => string;
  homeErrorTag: string;
  homeErrorTitle: string;
  homeErrorMeta: string;
  // Radar
  radarLessonCap: (title: string) => string;
  /** The skill first (what you practise), the lesson second (where). */
  radarSkillLessonCap: (skill: string, lesson: string) => string;
  radarPractise: string;
  radarSeeUnit: string;
  radarError: string;
}

export const CAN_DO_COPY: Record<UiLocale, CanDoCopy> = {
  es: {
    eyebrow: 'PROGRAMA GUIADO',
    title: 'Tu camino',
    back: 'Volver',
    close: 'Cerrar',
    retry: 'Reintentar',
    loadingProgram: 'Cargando el programa',
    pathError: 'No pudimos cargar tu camino.',
    // The owner's draft wording ("2 de 10 logrados": the can-dos achieved).
    levelAchievedTail: (total) => `de ${total} logrados`,
    skillsUsed: (used, total, level) =>
      `Usás ${used} de ${total} cosas que un ${level} hace en inglés`,
    levelTabs: 'Nivel',
    unitKicker: (order) => `Unidad ${order}`,
    unitMeta: (order, level) => `Unidad ${order} · ${level}`,
    firstStepsTab: 'Primeros pasos',
    firstStepsIntro:
      'Cuatro unidades cortas para arrancar de cero: frases que usás desde el primer día, con ayuda en español.',
    trackUnitMeta: (order) => `Primeros pasos · unidad ${order}`,
    unitStatus: {
      not_started: 'Sin empezar',
      in_progress: 'En curso',
      completed: 'Lograda',
      mastered: 'Dominada',
      coming_soon: 'Próximamente',
    },
    assumedChip: 'probablemente ya lo sabés · repasar',
    assumedNote: 'Está por debajo de tu nivel: probablemente ya lo sabés. Repasala cuando quieras.',
    nextChip: 'seguí acá',
    nextEyebrow: 'Tu próximo paso',
    nextReason: {
      continue_unit: 'Seguí donde quedaste',
      radar_focus: 'Lo pide tu radar',
      next_in_order: 'Lo que sigue en tu camino',
    },
    stepKind: { lesson: 'Lección', capstone: 'Escenario final' },
    start: 'Empezar',
    seeMission: 'Ver la misión',
    starting: 'Arrancando…',
    startError: 'No pudimos arrancar este paso. Probá de nuevo.',
    levelComplete: (level) => `Lograste todas las unidades de ${level}.`,
    noUnits: (level) =>
      `Las unidades de ${level} todavía están en preparación. Mientras tanto, podés recorrer las de otros niveles.`,
    libraryButton: 'Biblioteca de gramática',
    libraryButtonSub: 'Las estructuras, para leerlas cuando quieras',
    lessonsProgress: (done, total) => `${done} de ${total} lecciones`,
    capstoneShort: {
      not_started: 'escenario final pendiente',
      attempted: 'escenario final intentado',
      passed: 'escenario final pasado',
    },
    why: 'Por qué importa',
    loadingUnit: 'Cargando la unidad',
    unitError: 'No pudimos cargar la unidad.',
    lessonsEyebrow: 'Lecciones',
    lessonsDone: 'lecciones completas',
    focus: (label) => `Foco: ${label}`,
    lessonStatus: { available: 'Sin empezar', in_progress: 'En progreso', completed: 'Completada' },
    // The status pill beside it already says completed / not started.
    objectivesLine: (status, met, total) =>
      status === 'in_progress' ? `${met}/${total} objetivos` : `${total} objetivos`,
    alreadyUsed: 'ya lo usás · opcional',
    read: 'Leer',
    readingLabel: (title) => `Lectura: ${title}`,
    reviewAlso: 'Repasá también',
    practise: 'Practicar',
    review: 'Repasar',
    capstoneEyebrow: 'Escenario final',
    missionCount: (primary, bonus) =>
      `Misión: ${primary} ${primary === 1 ? 'objetivo' : 'objetivos'}${bonus > 0 ? ' + bonus' : ''}`,
    capstoneState: ({ status, ready, attempts, best, total }) =>
      status === 'passed'
        ? 'Pasado · la unidad cierra con esta escena'
        : status === 'attempted'
          ? `${attempts === 1 ? '1 intento' : `${attempts} intentos`} · tu mejor: ${best} de ${total}`
          : ready
            ? 'Listo: ya hiciste las lecciones'
            : 'Mejor después de las lecciones, pero está abierto',
    grammarEyebrow: 'Gramática de esta unidad',
    libraryLink: 'Ver toda la biblioteca',
    words: (used, total) => `Palabras de la unidad: ${used} de ${total}`,
    wordsHint: 'Las marcadas ya las usaste en una charla.',
    libraryTitle: 'Biblioteca de gramática',
    libraryIntro:
      'La gramática no es un paso del camino: está acá para leerla cuando la necesites, y cada tema te lleva a la lección que la practica.',
    yourLevel: 'Tu nivel',
    readings: (n) => (n === 1 ? '1 lectura' : `${n} lecturas`),
    practisedIn: 'Se practica en',
    notPractised: 'Todavía no hay una lección que la practique.',
    libraryEmpty: 'Todavía no hay lecturas para este nivel.',
    loadingLibrary: 'Cargando la biblioteca',
    libraryError: 'No pudimos cargar la biblioteca.',
    readingsLabel: 'Lecturas',
    loadingReading: 'Cargando la lectura',
    readingError: 'No pudimos cargar la lectura.',
    briefingLabel: 'ESCENARIO FINAL',
    briefingBack: 'Volver a la unidad',
    briefingUnit: 'Cierra la unidad',
    watchedTitle: 'También vamos a mirar',
    watchedNote: 'No son parte de la misión: si te salen, suman a tu progreso.',
    sceneStartError: 'No pudimos arrancar la escena. Probá de nuevo.',
    debriefEyebrow: 'Tu unidad',
    debriefStep: {
      lesson: (title) => `Lección: ${title}`,
      capstone: (title) => `Escenario final: ${title}`,
    },
    unitCompletedTitle: '¡Lograste la unidad!',
    unitCompletedSub: (canDo) => `Ya lo podés decir: «${canDo}»`,
    capstonePassedNow: '¡Pasaste el escenario final!',
    capstonePending: 'Estamos evaluando la misión…',
    continueWith: (title) => `Seguir con ${title}`,
    radarStepIn: (lesson) => `Lección «${lesson}» · Lo pide tu radar`,
    homeBadge: 'Programa',
    homeTitle: 'Tu plan de estudio',
    homeAll: 'Ver todo el programa',
    homeOpen: 'Seguir el programa',
    homeLevelComplete: (level) => `Lograste ${level}`,
    homeLevelCompleteMeta: 'Todas las unidades del nivel, logradas',
    homeNoUnitsTitle: 'Tu camino',
    homeNoUnitsMeta: (level) => `Las unidades de ${level} están en preparación`,
    homeErrorTag: 'Sin conexión',
    homeErrorTitle: 'No pudimos cargar tu plan',
    homeErrorMeta: 'Tocá para reintentar',
    radarLessonCap: (title) => `Esta lección lo trabaja: «${title}»`,
    radarSkillLessonCap: (skill, lesson) => `«${skill}» se practica en la lección «${lesson}»`,
    radarPractise: 'Practicá esta lección',
    radarSeeUnit: 'Ver la unidad',
    radarError: 'No pudimos buscar una lección para este foco.',
  },
  en: {
    eyebrow: 'GUIDED PROGRAM',
    title: 'Your path',
    back: 'Back',
    close: 'Close',
    retry: 'Try again',
    loadingProgram: 'Loading the program',
    pathError: "We couldn't load your path.",
    levelAchievedTail: (total) => `of ${total} achieved`,
    skillsUsed: (used, total, level) =>
      `You use ${used} of the ${total} things a ${level} speaker does in English`,
    levelTabs: 'Level',
    unitKicker: (order) => `Unit ${order}`,
    unitMeta: (order, level) => `Unit ${order} · ${level}`,
    firstStepsTab: 'First steps',
    firstStepsIntro:
      'Four short units to start from zero: phrases you use from day one, with help in Spanish.',
    trackUnitMeta: (order) => `First steps · unit ${order}`,
    unitStatus: {
      not_started: 'Not started',
      in_progress: 'In progress',
      completed: 'Achieved',
      mastered: 'Mastered',
      coming_soon: 'Coming soon',
    },
    assumedChip: 'you probably know this · review',
    assumedNote:
      "It's below your level: you probably know it already. Review it whenever you like.",
    nextChip: 'continue here',
    nextEyebrow: 'Your next step',
    nextReason: {
      continue_unit: 'Pick up where you left off',
      radar_focus: 'Your radar asks for it',
      next_in_order: 'Next on your path',
    },
    stepKind: { lesson: 'Lesson', capstone: 'Final scenario' },
    start: 'Start',
    seeMission: 'See the mission',
    starting: 'Starting…',
    startError: "We couldn't start this step. Try again.",
    levelComplete: (level) => `You've achieved every ${level} unit.`,
    noUnits: (level) =>
      `The ${level} units are still being prepared. Meanwhile, you can go through other levels.`,
    libraryButton: 'Grammar library',
    libraryButtonSub: 'The structures, to read whenever you like',
    lessonsProgress: (done, total) => `${done} of ${total} lessons`,
    capstoneShort: {
      not_started: 'final scenario pending',
      attempted: 'final scenario attempted',
      passed: 'final scenario passed',
    },
    why: 'Why it matters',
    loadingUnit: 'Loading the unit',
    unitError: "We couldn't load the unit.",
    lessonsEyebrow: 'Lessons',
    lessonsDone: 'lessons done',
    focus: (label) => `Focus: ${label}`,
    lessonStatus: { available: 'Not started', in_progress: 'In progress', completed: 'Completed' },
    objectivesLine: (status, met, total) =>
      status === 'in_progress' ? `${met}/${total} objectives` : `${total} objectives`,
    alreadyUsed: 'you already use it · optional',
    read: 'Read',
    readingLabel: (title) => `Reading: ${title}`,
    reviewAlso: 'Also review',
    practise: 'Practise',
    review: 'Review',
    capstoneEyebrow: 'Final scenario',
    missionCount: (primary, bonus) =>
      `Mission: ${primary} ${primary === 1 ? 'objective' : 'objectives'}${bonus > 0 ? ' + bonus' : ''}`,
    capstoneState: ({ status, ready, attempts, best, total }) =>
      status === 'passed'
        ? 'Passed · this scene closes the unit'
        : status === 'attempted'
          ? `${attempts === 1 ? '1 attempt' : `${attempts} attempts`} · your best: ${best} of ${total}`
          : ready
            ? "Ready: you've done the lessons"
            : "Best after the lessons, but it's open",
    grammarEyebrow: 'Grammar in this unit',
    libraryLink: 'See the whole library',
    words: (used, total) => `Unit words: ${used} of ${total}`,
    wordsHint: "The highlighted ones you've already used in a conversation.",
    libraryTitle: 'Grammar library',
    libraryIntro:
      "Grammar is not a step of the path: it's here to read whenever you need it, and each topic takes you to the lesson that practises it.",
    yourLevel: 'Your level',
    readings: (n) => (n === 1 ? '1 reading' : `${n} readings`),
    practisedIn: 'Practised in',
    notPractised: 'No lesson practises it yet.',
    libraryEmpty: 'There are no readings for this level yet.',
    loadingLibrary: 'Loading the library',
    libraryError: "We couldn't load the library.",
    readingsLabel: 'Readings',
    loadingReading: 'Loading the reading',
    readingError: "We couldn't load the reading.",
    briefingLabel: 'FINAL SCENARIO',
    briefingBack: 'Back to the unit',
    briefingUnit: 'Closes the unit',
    watchedTitle: "We'll also watch",
    watchedNote:
      "They aren't part of the mission: if they come up, they count toward your progress.",
    sceneStartError: "We couldn't start the scene. Try again.",
    debriefEyebrow: 'Your unit',
    debriefStep: {
      lesson: (title) => `Lesson: ${title}`,
      capstone: (title) => `Final scenario: ${title}`,
    },
    unitCompletedTitle: 'You achieved the unit!',
    unitCompletedSub: (canDo) => `Now you can say: "${canDo}"`,
    capstonePassedNow: 'You passed the final scenario!',
    capstonePending: "We're scoring the mission…",
    continueWith: (title) => `Continue with ${title}`,
    radarStepIn: (lesson) => `Lesson "${lesson}" · Your radar asks for it`,
    homeBadge: 'Program',
    homeTitle: 'Your study plan',
    homeAll: 'See the whole program',
    homeOpen: 'Continue the program',
    homeLevelComplete: (level) => `You achieved ${level}`,
    homeLevelCompleteMeta: 'Every unit of the level, achieved',
    homeNoUnitsTitle: 'Your path',
    homeNoUnitsMeta: (level) => `The ${level} units are being prepared`,
    homeErrorTag: 'Offline',
    homeErrorTitle: "We couldn't load your plan",
    homeErrorMeta: 'Tap to try again',
    radarLessonCap: (title) => `This lesson works on it: "${title}"`,
    radarSkillLessonCap: (skill, lesson) => `"${skill}" is practised in the lesson "${lesson}"`,
    radarPractise: 'Practise this lesson',
    radarSeeUnit: 'See the unit',
    radarError: "We couldn't look for a lesson for this focus.",
  },
};

/** A curriculum string in the UI locale. */
export function loc(text: LocalizedText, locale: UiLocale = 'es'): string {
  return text[locale];
}
